import { motion } from 'framer-motion';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const BusinessOutreachPrivacy = () => {
  return (
    <div data-testid="business-outreach-privacy-page">
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp}>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-primary mb-6">
              Business Outreach Privacy Notice
            </h1>
            <p className="text-muted-foreground mb-8">
              Last updated: 26 April 2026
            </p>

            <div className="prose prose-slate max-w-none space-y-8">
              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Who we are</h2>
                <p className="text-muted-foreground mb-3">
                  Dockside Design is a trading name of Altorite LTD, a company registered in England and Wales.
                </p>
                <p className="text-muted-foreground mb-3">
                  We provide web design, website management, and related digital services to businesses in the United Kingdom.
                </p>
                <p className="text-muted-foreground">
                  You can contact us at:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground mt-2">
                  <li>
                    Email:{' '}
                    <a href="mailto:support@docksidedesign.co.uk" className="text-accent hover:underline">
                      support@docksidedesign.co.uk
                    </a>
                  </li>
                  <li>Website: www.docksidedesign.co.uk</li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">What this notice covers</h2>
                <p className="text-muted-foreground mb-3">
                  This notice explains how we handle personal data when we contact businesses about our services.
                </p>
                <p className="text-muted-foreground mb-3">
                  This may include situations where we identify a business that may benefit from website design, website improvement, hosting, maintenance, or related digital services.
                </p>
                <p className="text-muted-foreground">
                  This notice is separate from our main{' '}
                  <a href="/privacy" className="text-accent hover:underline">privacy policy</a>, which covers website visitors, contact form submissions, cookies, analytics, and general enquiries.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">What information we may collect</h2>
                <p className="text-muted-foreground mb-3">
                  When carrying out business outreach, we may collect and use limited business contact information, including:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>business name</li>
                  <li>business website address</li>
                  <li>business email address</li>
                  <li>business phone number</li>
                  <li>business location or service area</li>
                  <li>contact name or job title, if publicly available</li>
                  <li>notes about the business website or online presence</li>
                  <li>Companies House information, where relevant</li>
                  <li>whether we have contacted the business before</li>
                  <li>whether the business has asked us not to contact them again</li>
                </ul>
                <p className="text-muted-foreground mt-3">
                  We do not intentionally collect sensitive personal data for outreach.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Where we collect information from</h2>
                <p className="text-muted-foreground mb-3">
                  We may collect information from publicly available business sources, including:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>business websites</li>
                  <li>public contact pages</li>
                  <li>online business directories</li>
                  <li>search engine results</li>
                  <li>Companies House</li>
                  <li>social media business pages</li>
                  <li>other publicly available business listings</li>
                </ul>
                <p className="text-muted-foreground mt-3">
                  We only use information where we believe it is relevant to business-to-business outreach.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Why we use this information</h2>
                <p className="text-muted-foreground mb-3">
                  We use this information to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>identify businesses that may be interested in our services</li>
                  <li>contact businesses about relevant website or digital service improvements</li>
                  <li>keep a record of who we have contacted</li>
                  <li>avoid contacting businesses that have opted out</li>
                  <li>manage our sales and business development activity</li>
                  <li>comply with legal and regulatory obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Our lawful basis</h2>
                <p className="text-muted-foreground mb-3">
                  Where we process personal data for business outreach, we rely on legitimate interests under UK GDPR Article 6(1)(f).
                </p>
                <p className="text-muted-foreground mb-3">
                  Our legitimate interest is promoting our services to relevant businesses in a targeted and proportionate way.
                </p>
                <p className="text-muted-foreground mb-3">
                  Before contacting a business, we aim to consider whether the contact is relevant, whether the recipient would reasonably expect business-related contact, and whether their rights and interests override our interests.
                </p>
                <p className="text-muted-foreground mb-3">
                  For electronic marketing, we also consider the Privacy and Electronic Communications Regulations (PECR), including whether the recipient appears to be a corporate subscriber or whether another lawful route is required.
                </p>
                <p className="text-muted-foreground">
                  The ICO explains that direct marketing may be a legitimate interest, but this depends on the circumstances and does not override PECR where PECR requires consent.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Business outreach emails</h2>
                <p className="text-muted-foreground mb-3">
                  Where we send a business outreach email, we aim to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>clearly identify who we are</li>
                  <li>explain why we are contacting the business</li>
                  <li>keep the message relevant to the recipient's business</li>
                  <li>provide a valid way to opt out</li>
                  <li>stop contacting the business if they ask us not to contact them again</li>
                </ul>
                <p className="text-muted-foreground mt-3">
                  The ICO states that the PECR rule on direct marketing by electronic mail does not apply to corporate subscribers, such as limited companies and LLPs, although UK GDPR still applies where personal data is processed.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Live business calls</h2>
                <p className="text-muted-foreground">
                  Where we make live business calls, we aim to follow applicable PECR rules, including checking relevant preference services where required and respecting any objections or do-not-contact requests.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">How to opt out</h2>
                <p className="text-muted-foreground mb-3">
                  You can ask us not to contact you for business outreach at any time.
                </p>
                <p className="text-muted-foreground mb-3">To opt out, you can:</p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>reply to any outreach email and ask us not to contact you again</li>
                  <li>
                    email us at{' '}
                    <a href="mailto:support@docksidedesign.co.uk" className="text-accent hover:underline">
                      support@docksidedesign.co.uk
                    </a>
                  </li>
                </ul>
                <p className="text-muted-foreground mt-3">
                  Once you opt out, we will add your details to a suppression list so that we know not to contact you again for business outreach.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">How long we keep outreach data</h2>
                <p className="text-muted-foreground mb-3">
                  We usually keep business outreach records for up to 12 months from the last interaction, unless:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>the business becomes a client</li>
                  <li>we need to keep the information for legal, accounting, or contractual reasons</li>
                  <li>the business asks us not to contact them again</li>
                </ul>
                <p className="text-muted-foreground mt-3">
                  If you ask us not to contact you again, we may keep limited information on a suppression list so we can respect that request.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Who we share information with</h2>
                <p className="text-muted-foreground mb-3">
                  We do not sell business outreach data.
                </p>
                <p className="text-muted-foreground mb-3">
                  We may share limited data with trusted service providers that help us operate our business, such as:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>email providers</li>
                  <li>website hosting providers</li>
                  <li>spreadsheet or CRM tools</li>
                  <li>cloud storage providers</li>
                </ul>
                <p className="text-muted-foreground mt-3">
                  These providers only process information on our behalf where necessary.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Your rights</h2>
                <p className="text-muted-foreground mb-3">
                  Under UK data protection law, you may have the right to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>request access to personal data we hold about you</li>
                  <li>ask us to correct inaccurate information</li>
                  <li>ask us to delete your information</li>
                  <li>object to our use of your information for direct marketing</li>
                  <li>restrict how we use your information</li>
                  <li>complain to the Information Commissioner's Office</li>
                </ul>
                <p className="text-muted-foreground mt-3">
                  You can exercise your rights by contacting{' '}
                  <a href="mailto:support@docksidedesign.co.uk" className="text-accent hover:underline">
                    support@docksidedesign.co.uk
                  </a>.
                </p>
                <p className="text-muted-foreground mt-3">
                  You also have the right to complain to the ICO at{' '}
                  <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                    ico.org.uk
                  </a>.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Changes to this notice</h2>
                <p className="text-muted-foreground">
                  We may update this notice from time to time. Any changes will be posted on this page with an updated date.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BusinessOutreachPrivacy;
